/**
 * A one-slot mailbox between a native listener and the React hook that acts
 * on it. Pure, so it is tested in Node.
 *
 * A tap that cold-starts the app arrives before the router has mounted (and
 * before the session gate has decided): there is nobody to navigate yet. The
 * listener `deliver`s into this inbox; the hook `attach`es once the router is
 * ready and receives what waited. Only the LATEST item is kept — two taps
 * before the router mounted mean the person wants the second one, and
 * replaying both would push two entries for one gesture.
 */
export interface Inbox<T> {
  /**
   * Become the handler: whatever waited is handed over at once, then every
   * later delivery. Returns the detach. A second `attach` replaces the first.
   */
  attach(handler: (item: T) => void): () => void;
  /** Hand the item to the attached handler, or keep it (latest wins). */
  deliver(item: T): void;
  /** Drop what waited, without handing it to anyone. */
  drop(): void;
}

export function createInbox<T>(): Inbox<T> {
  let waiting: { item: T } | null = null;
  let handler: ((item: T) => void) | null = null;

  return {
    attach(next) {
      handler = next;
      if (waiting !== null) {
        const { item } = waiting;
        waiting = null;
        next(item);
      }
      return () => {
        if (handler === next) {
          handler = null;
        }
      };
    },
    deliver(item) {
      if (handler === null) {
        waiting = { item };
        return;
      }
      handler(item);
    },
    drop() {
      waiting = null;
    },
  };
}
